import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ShieldCheck, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { createAgentRequestPublic } from '../../lib/api';

const BecomeAgent = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    experience: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createAgentRequestPublic({
        full_name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        experience_years: Number(form.experience || 0),
        affiliations: form.message.trim() || null,
        source_page: '/become-agent',
      });
      toast.success('Application submitted successfully');
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        city: '',
        experience: '',
        message: '',
      });
    } catch (err) {
      toast.error(err?.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900/50 ">

      {/* ── Hero ── */}
      <div className="bg-slate-900 text-white py-20 mb-16 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-sky-950 opacity-80" />
        <div className="relative z-10 container mx-auto px-4">
          <span className="inline-block bg-sky-500/15 text-sky-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">Become Agent</span>
          <h1 className="text-4xl md:text-5xl font-bold font-['Plus_Jakarta_Sans'] mb-4">Become Agent</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">Join India's fastest-growing DSA network. Get access to 50+ lenders, instant payouts, and state-of-the-art CRM tools.</p>
        </div>
      </div>
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-12 lg:gap-24">

          <div className="flex-1 space-y-8">
            <div>
              <div className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-sm text-emerald-600 dark:text-emerald-400 mb-6">
                <ShieldCheck className="mr-2 h-4 w-4" />
                Trusted Partner Program
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight font-['Plus_Jakarta_Sans'] mb-4 text-slate-900 dark:text-white">
                Start your journey as a financial consultant
              </h1>
              <p className="text-lg text-slate-600 dark:text-slate-400">
                Join India's fastest-growing DSA network. Get access to 50+ lenders, instant payouts, and state-of-the-art CRM tools.
              </p>
            </div>

            <div className="space-y-6">
              {[
                'Zero setup cost or hidden fees',
                'Highest payout slabs in the industry',
                'Dedicated relationship manager support',
                'Real-time status tracking via Dashboard'
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center dark:bg-emerald-900/50">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="text-slate-700 dark:text-slate-300 font-medium">{benefit}</span>
                </div>
              ))}
            </div>

          </div>

          <div className="flex-1">
            <Card className="border-0 shadow-2xl bg-white dark:bg-slate-800 relative z-10 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

              <CardHeader className="p-8 pb-0">
                <CardTitle className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Apply Now</CardTitle>
                <CardDescription>Fill in your details and our team will contact you within 24 hours.</CardDescription>
              </CardHeader>

              <CardContent className="p-8">
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input id="firstName" placeholder="Enter first name" value={form.firstName} onChange={handleChange('firstName')} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input id="lastName" placeholder="Enter last name" value={form.lastName} onChange={handleChange('lastName')} required />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input id="email" type="email" placeholder="agent@example.com" value={form.email} onChange={handleChange('email')} required />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input id="phone" type="tel" placeholder="10-digit mobile number" value={form.phone} onChange={handleChange('phone')} required />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input id="city" placeholder="Current city" value={form.city} onChange={handleChange('city')} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="experience">Experience (Years)</Label>
                      <Input id="experience" type="number" placeholder="e.g. 2" min="0" value={form.experience} onChange={handleChange('experience')} required />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Any current affiliations? (Optional)</Label>
                    <Textarea id="message" placeholder="Mention any banks or NBFCs you currently work with..." className="resize-none" rows={3} value={form.message} onChange={handleChange('message')} />
                  </div>

                  <Button type="submit" size="lg" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Application'} {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BecomeAgent;
